import React, { useState, useEffect } from 'react';
import { useShift } from '../../../context/ShiftContext';
import { useTeam } from '../../../context/TeamContext';
import { useAuth } from '../../../context/AuthContext';
import { collection, query, where, getDocs, orderBy } from 'firebase/firestore';
import { db } from '../../../firebase/firebaseConfig';
import { SHIFT_STATUS } from '../../../data/nurseAppModels';

const SwapShiftModal = ({ isOpen, onClose, shiftId, shiftData }) => {
  const { requestShiftSwap } = useShift();
  const { currentTeam } = useTeam();
  const { currentUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [teamMembers, setTeamMembers] = useState([]);
  const [availableShifts, setAvailableShifts] = useState([]);
  const [loadingMembers, setLoadingMembers] = useState(true);

  const [formData, setFormData] = useState({
    requestedTo: '',
    requestedShiftId: '',
    message: ''
  });

  // Fetch team members (excluding current user)
  useEffect(() => {
    const fetchTeamMembers = async () => {
      if (!currentTeam) return;

      try {
        setLoadingMembers(true);
        const members = currentTeam.members || [];
        // Filter out current user
        const otherMembers = members.filter(m => m.uid !== currentUser?.uid);
        setTeamMembers(otherMembers);
      } catch (err) {
        console.error('Error fetching team members:', err);
      } finally {
        setLoadingMembers(false);
      }
    };

    if (isOpen && currentTeam) {
      fetchTeamMembers();
    }
  }, [isOpen, currentTeam, currentUser]);

  // Fetch available shifts when user is selected
  useEffect(() => {
    const fetchAvailableShifts = async () => {
      if (!formData.requestedTo || !currentTeam) {
        setAvailableShifts([]);
        return;
      }

      try {
        // Fetch shifts assigned to the selected user that are scheduled
        const shiftsRef = collection(db, 'shifts');
        const q = query(
          shiftsRef,
          where('teamId', '==', currentTeam.id),
          where('assignedTo', '==', formData.requestedTo),
          where('status', '==', SHIFT_STATUS.SCHEDULED),
          orderBy('startTime', 'asc')
        );
        const snapshot = await getDocs(q);
        const shifts = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setAvailableShifts(shifts);
      } catch (err) {
        console.error('Error fetching available shifts:', err);
        setAvailableShifts([]);
      }
    };

    if (formData.requestedTo) {
      fetchAvailableShifts();
    }
  }, [formData.requestedTo, currentTeam]);

  // Reset form when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setFormData({
        requestedTo: '',
        requestedShiftId: '',
        message: ''
      });
      setError(null);
    }
  }, [isOpen]);

  // Handle form input change
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Handle form submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      if (!formData.requestedTo) {
        throw new Error('กรุณาเลือกสมาชิกที่ต้องการแลกเวรด้วย');
      }

      // Get selected member info
      const selectedMember = teamMembers.find(m => m.uid === formData.requestedTo);
      if (!selectedMember) {
        throw new Error('ไม่พบสมาชิกที่เลือก');
      }

      // Get selected shift info (if provided)
      let requestedShiftId = null;
      if (formData.requestedShiftId) {
        requestedShiftId = formData.requestedShiftId;
      }

      const swapData = {
        requestedTo: formData.requestedTo,
        requestedToName: selectedMember.name,
        requestedShiftId: requestedShiftId,
        message: formData.message || ''
      };

      const result = await requestShiftSwap(shiftId, swapData);

      if (result.success) {
        // Close modal and refresh
        onClose(true); // Pass true to indicate success
      } else {
        setError(result.error || 'เกิดข้อผิดพลาดในการส่งคำขอแลกเวร');
      }
    } catch (err) {
      setError(err.message || 'เกิดข้อผิดพลาดในการส่งคำขอแลกเวร');
    } finally {
      setLoading(false);
    }
  };

  // Format date and time
  const formatDateTime = (timestamp) => {
    if (!timestamp) return '-';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleString('th-TH', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-md w-full p-6 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="mb-6">
          <h2 className="text-xl font-bold text-gray-800">ขอแลกเวร</h2>
          <p className="text-sm text-gray-600 mt-1">ส่งคำขอแลกเวรกับสมาชิกทีม</p>
        </div>

        {/* Current Shift Info */}
        {shiftData && (
          <div className="mb-6 p-4 bg-blue-50 rounded-lg">
            <h3 className="font-medium text-gray-700 mb-2">เวรที่ต้องการแลก:</h3>
            <div className="text-sm text-gray-600">
              <p>📅 {formatDateTime(shiftData.startTime)}</p>
              <p>⏰ {shiftData.shiftType === 'MORNING' ? 'เช้า' : shiftData.shiftType === 'AFTERNOON' ? 'บ่าย' : 'ดึก'}</p>
              {shiftData.department && <p>🏥 {shiftData.department}</p>}
            </div>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-red-600 text-sm">{error}</p>
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit}>
          {/* Select Member */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              เลือกสมาชิกที่ต้องการแลกเวรด้วย *
            </label>
            {loadingMembers ? (
              <div className="text-sm text-gray-500">กำลังโหลด...</div>
            ) : teamMembers.length === 0 ? (
              <div className="text-sm text-gray-500">ไม่มีสมาชิกในทีม</div>
            ) : (
              <select
                name="requestedTo"
                value={formData.requestedTo}
                onChange={handleChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">เลือกสมาชิก...</option>
                {teamMembers.map((member) => (
                  <option key={member.uid} value={member.uid}>
                    {member.name}
                  </option>
                ))}
              </select>
            )}
          </div>

          {/* Select Shift (Optional) */}
          {formData.requestedTo && availableShifts.length > 0 && (
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                เลือกเวรที่ต้องการแลก <span className="text-gray-500">(ไม่บังคับ)</span>
              </label>
              <select
                name="requestedShiftId"
                value={formData.requestedShiftId}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">ไม่ระบุ (แลกแบบ one-way)</option>
                {availableShifts.map((shift) => (
                  <option key={shift.id} value={shift.id}>
                    {formatDateTime(shift.startTime)} - {shift.shiftType === 'MORNING' ? 'เช้า' : shift.shiftType === 'AFTERNOON' ? 'บ่าย' : 'ดึก'}
                  </option>
                ))}
              </select>
              <p className="text-xs text-gray-500 mt-1">
                ถ้าไม่เลือก จะเป็นการแลกแบบ one-way (คุณจะได้เวรของเขา แต่เขาไม่ได้เวรของคุณ)
              </p>
            </div>
          )}

          {/* Message */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              ข้อความ/เหตุผล <span className="text-gray-500">(ไม่บังคับ)</span>
            </label>
            <textarea
              name="message"
              value={formData.message}
              onChange={handleChange}
              rows="3"
              placeholder="ระบุเหตุผลหรือข้อความถึงสมาชิก..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          {/* Submit Buttons */}
          <div className="flex gap-3">
            <button
              type="button"
              onClick={() => onClose(false)}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition"
            >
              ยกเลิก
            </button>
            <button
              type="submit"
              disabled={loading || !formData.requestedTo}
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'กำลังส่ง...' : 'ส่งคำขอ'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SwapShiftModal;



